/**
 * Navigation structure — the single source of truth for the rail
 * (CustomMenu), the section bar (SectionTabs), the breadcrumb (CustomAppBar)
 * and every page that renders a section (`useActiveSection`).
 *
 * A destination is a rail entry; its sections are the second navigation level,
 * shown in the section bar anchored under the app bar (hybrid-3c §5.1). A
 * section is addressed either by a `?tab=` query on the destination's path or
 * by a sub-path; `paths` lists the resource routes (edit/create pages) that
 * belong to it, so a detail page keeps its destination and section lit.
 */

import type { ComponentType } from 'react';
import {
  DashboardRounded as DashboardIcon,
  Business as AccountIcon,
  BarChart as StatsIcon,
  PhotoCamera as PhotoIcon,
  Tune as KycConfigIcon,
  Payments as BillingIcon,
  Settings as SystemIcon,
  Science as OcrTestIcon,
  MenuBookRounded as DevelopersIcon,
} from '@mui/icons-material';

export type Role = 'Administrator' | 'AdvancedUser' | 'User';

const ROLE_LEVEL: Record<Role, number> = { Administrator: 3, AdvancedUser: 2, User: 1 };

export interface MenuTab {
  /** The section's id: the `?tab=` value, or the last segment of its sub-path. */
  key: string;
  /** Where the section lives. */
  to: string;
  /** i18n key of the label, read with `smart_count: 2`. */
  labelKey: string;
  /** Minimum role that sees the section. */
  minRole?: Role;
  /** Resource routes (edit/create) that belong to this section. */
  paths?: string[];
}

export interface MenuEntry {
  name: string;
  to: string;
  icon: ComponentType;
  nameKey: string;
  /** Minimum role that sees the entry. */
  minRole?: Role;
  /** A hairline above the entry, grouping the rail by how often it is used. */
  separatorBefore?: boolean;
  tabs?: MenuTab[];
}

/** The Developers destination: the only one a documentation-only user sees. */
export const DEVELOPERS_ENTRY = 'developers';

export const MENU_ENTRIES: MenuEntry[] = [
  {
    name: 'dashboard',
    to: '/',
    icon: DashboardIcon,
    nameKey: 'ra.page.dashboard',
    tabs: [
      { key: 'panel', to: '/', labelKey: 'dashboard.tabs.panel' },
      { key: 'overview', to: '/?tab=overview', labelKey: 'tech_support.tabs.overview', minRole: 'AdvancedUser' },
      { key: 'services', to: '/?tab=services', labelKey: 'tech_support.tabs.servers', minRole: 'AdvancedUser' },
      { key: 'stress-test', to: '/?tab=stress-test', labelKey: 'tech_support.tabs.stress_test', minRole: 'AdvancedUser' },
    ],
  },
  {
    name: 'accounts',
    to: '/accounts',
    icon: AccountIcon,
    nameKey: 'resources.accounts.name',
    separatorBefore: true,
  },
  { name: 'statistics', to: '/statistics', icon: StatsIcon, nameKey: 'resources.statistics.name' },
  {
    name: 'photo-upload',
    to: '/photo-upload',
    icon: PhotoIcon,
    nameKey: 'menu.photo_upload',
    separatorBefore: true,
  },
  {
    name: 'ocr-testing',
    to: '/ocr-testing',
    icon: OcrTestIcon,
    nameKey: 'menu.ocr_testing',
    minRole: 'Administrator',
    tabs: [
      { key: 'cases', to: '/ocr-testing/cases', labelKey: 'ocr_testing.tabs.cases' },
      { key: 'runs', to: '/ocr-testing/runs', labelKey: 'ocr_testing.tabs.runs' },
    ],
  },
  {
    name: 'kyc-config',
    to: '/kyc-config',
    icon: KycConfigIcon,
    nameKey: 'menu.kyc_config',
    minRole: 'Administrator',
    separatorBefore: true,
    tabs: [
      { key: 'document-types', to: '/kyc-config?tab=document-types', labelKey: 'resources.document-types.name', paths: ['/document-types'] },
      { key: 'nationalities', to: '/kyc-config?tab=nationalities', labelKey: 'resources.nationalities.name', paths: ['/nationalities'] },
      { key: 'kyc-fields', to: '/kyc-config?tab=kyc-fields', labelKey: 'resources.kyc-fields.name', paths: ['/kyc-fields'] },
      { key: 'fields-mapping', to: '/kyc-config?tab=fields-mapping', labelKey: 'resources.fields-mapping.name', paths: ['/fields-mapping'] },
      { key: 'default-mapping', to: '/kyc-config?tab=default-mapping', labelKey: 'resources.default-mapping.name', paths: ['/default-mapping'] },
    ],
  },
  {
    name: 'billing',
    to: '/billing',
    icon: BillingIcon,
    nameKey: 'menu.billing',
    minRole: 'AdvancedUser',
    tabs: [
      { key: 'cost-centers', to: '/billing?tab=cost-centers', labelKey: 'resources.cost-centers.name', paths: ['/cost-centers'] },
      { key: 'credits', to: '/billing?tab=credits', labelKey: 'menu.credits', paths: ['/credits'] },
      { key: 'pricing', to: '/billing?tab=pricing', labelKey: 'menu.pricing', minRole: 'Administrator', paths: ['/pricing'] },
    ],
  },
  {
    name: 'system',
    to: '/system',
    icon: SystemIcon,
    nameKey: 'menu.system',
    minRole: 'Administrator',
    tabs: [
      { key: 'servers', to: '/system?tab=servers', labelKey: 'resources.servers.name', paths: ['/servers'] },
      { key: 'licenses', to: '/system?tab=licenses', labelKey: 'resources.licenses.name', paths: ['/licenses'] },
      { key: 'users', to: '/system?tab=users', labelKey: 'resources.users.name', paths: ['/users'] },
      { key: 'backups', to: '/system?tab=backups', labelKey: 'menu.backups' },
    ],
  },
  {
    // Last and in a group of its own: it documents the API, it is not part of running the
    // product. A documentation-only user sees this entry alone (CustomMenu).
    name: DEVELOPERS_ENTRY,
    to: '/developers',
    icon: DevelopersIcon,
    nameKey: 'menu.developers',
    separatorBefore: true,
    tabs: [
      { key: 'overview', to: '/developers?tab=overview', labelKey: 'developers.tabs.overview' },
      { key: 'reference', to: '/developers?tab=reference', labelKey: 'developers.tabs.reference' },
      { key: 'recipes', to: '/developers?tab=recipes', labelKey: 'developers.tabs.recipes' },
      { key: 'downloads', to: '/developers?tab=downloads', labelKey: 'developers.tabs.downloads' },
    ],
  },
];

export const userLevel = (role?: string | null): number => ROLE_LEVEL[(role || '') as Role] || 0;

const meetsRole = (minRole: Role | undefined, role?: string | null): boolean =>
  !minRole || userLevel(role) >= ROLE_LEVEL[minRole];

export const isEntryVisible = (entry: MenuEntry, role?: string | null): boolean => meetsRole(entry.minRole, role);

export const visibleTabs = (entry: MenuEntry, role?: string | null): MenuTab[] =>
  (entry.tabs ?? []).filter((tab) => meetsRole(tab.minRole, role));

const pathOf = (to: string): string => to.split('?')[0];
const tabParamOf = (to: string): string | null => new URLSearchParams(to.split('?')[1] ?? '').get('tab');

const startsWithPath = (pathname: string, base: string): boolean =>
  base === '/' ? pathname === '/' : pathname === base || pathname.startsWith(`${base}/`);

/** Whether the path belongs to one of the section's resource routes. */
const isOnTabResource = (pathname: string, tab: MenuTab): boolean =>
  (tab.paths ?? []).some((path) => startsWithPath(pathname, path));

export interface ResolvedLocation {
  entry: MenuEntry;
  tab?: MenuTab;
  /** The route sits below the section itself: an edit, create or detail page. */
  depth: 'section' | 'create' | 'detail';
}

/**
 * Resolve a location to its destination and section. A `?tab=` section with no
 * `tab` in the query is the destination's first section the reader can see, so
 * the bare destination URL and the first section are the same page.
 */
export const resolveLocation = (pathname: string, search: string, role?: string | null): ResolvedLocation | null => {
  // Up to the first slash: the API reference viewer appends its own route to the Developers
  // address (`?tab=reference/tag/...`), and the section must still resolve.
  const tabParam = new URLSearchParams(search).get('tab')?.split('/')[0] ?? null;

  for (const entry of MENU_ENTRIES) {
    const tabs = visibleTabs(entry, role);

    const resourceTab = tabs.find((tab) => isOnTabResource(pathname, tab));
    if (resourceTab) {
      return { entry, tab: resourceTab, depth: /\/(create|new)$/.test(pathname) ? 'create' : 'detail' };
    }

    if (!startsWithPath(pathname, entry.to)) continue;

    const queryTabs = tabs.filter((tab) => tabParamOf(tab.to) !== null || pathOf(tab.to) === entry.to);
    const pathTab = tabs.find((tab) => pathOf(tab.to) !== entry.to && startsWithPath(pathname, pathOf(tab.to)));
    if (pathTab) {
      const rest = pathname.slice(pathOf(pathTab.to).length);
      const depth = rest === '' || rest === '/' ? 'section' : /\/(create|new)$/.test(rest) ? 'create' : 'detail';
      return { entry, tab: pathTab, depth };
    }

    if (pathname === entry.to && queryTabs.length > 0) {
      const tab = queryTabs.find((candidate) => (tabParamOf(candidate.to) ?? null) === tabParam) ?? queryTabs[0];
      return { entry, tab, depth: 'section' };
    }

    // A path-sectioned destination opened at its root shows its first section.
    return { entry, tab: tabs[0], depth: 'section' };
  }

  return null;
};

/** The active section's key on the current location, for the page that renders the sections. */
export const activeTabKey = (pathname: string, search: string, role?: string | null): string | undefined =>
  resolveLocation(pathname, search, role)?.tab?.key;
